import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { UploadIcon } from "@/components/icons";
import { formatBytes } from "@/lib/bytes";
import { cn } from "@/lib/cn";

/**
 * What the upload control offers (FILE-2). The server decides what a file is
 * from its bytes, so this list only filters the picker; a `.db` that is really
 * DuckDB, or a `.txt` that is TSV, is still recognised for what it is.
 */
export const ACCEPTED_FILES = [
  ".sqlite",
  ".sqlite3",
  ".db",
  ".duckdb",
  ".csv",
  ".tsv",
  ".tab",
  ".txt",
  ".xlsx",
  ".parquet",
  ".json",
  ".ndjson",
  ".jsonl",
].join(",");

export const ACCEPTED_SUMMARY =
  "SQLite or DuckDB databases, CSV/TSV, Excel (.xlsx), Parquet or JSON.";

/**
 * A drop zone that is also a button: dropping a file, clicking, and pressing
 * Enter on it all choose one. It holds no upload state of its own — the
 * caller owns the file, the progress and the error, because the same control
 * creates a source on one page and replaces a file on another.
 */
export function FileDrop({
  file,
  onFile,
  progress,
  disabled = false,
  hint = ACCEPTED_SUMMARY,
  label = "Drop a file here, or choose one",
  children,
}: {
  file: File | null;
  onFile: (file: File) => void;
  /** 0–1 while uploading, null otherwise. */
  progress: number | null;
  disabled?: boolean;
  hint?: ReactNode;
  label?: string;
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [over, setOver] = useState(false);

  const take = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-describedby={hintId}
        onClick={() => !disabled && input.current?.click()}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            input.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={take}
        className={cn(
          "flex flex-col items-center gap-2 rounded-container border border-dashed px-6 py-8 text-center motion-fast",
          over ? "border-accent bg-accent-subtle" : "border-border bg-surface",
          disabled ? "opacity-60" : "cursor-pointer hover:border-border-emphasized hover:bg-hover",
        )}
      >
        <span className="text-ink-disabled">
          <UploadIcon className="size-6" />
        </span>
        <span className="type-label text-ink">{file ? file.name : label}</span>
        <span id={hintId} className="type-support text-ink-secondary">
          {file ? formatBytes(file.size) : hint}
        </span>
        <input
          ref={input}
          type="file"
          accept={ACCEPTED_FILES}
          className="sr-only"
          tabIndex={-1}
          disabled={disabled}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) onFile(chosen);
            event.target.value = "";
          }}
        />
      </div>
      {progress !== null ? (
        <div className="flex items-center gap-3" aria-live="polite">
          <div
            role="progressbar"
            aria-label="Upload progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="bg-muted h-1.5 flex-1 overflow-hidden rounded-full"
          >
            <div
              className="bg-accent h-full rounded-full motion-fast"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <span className="type-caption text-ink-secondary tabular-nums">
            {progress < 1 ? `${Math.round(progress * 100)}%` : "importing…"}
          </span>
        </div>
      ) : null}
      {children}
    </div>
  );
}
